import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAgendaSdrs, getAssignableClients, getWeekSchedule, assignScheduleDays, removeScheduleDay,
  getCapacityBoard, setSdrCapacity, isAbsenceDayType,
  getTtRequests, requestTeletravail, decideTtRequest,
  type SdrOption, type AssignableClient, type ScheduleCell, type CapacityBoard, type SdrCapacityRow, type TtRequest,
} from '../../lib/refonteApi';
import { SkeletonRows, SkeletonCards } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';
import { useAuth } from '../../components/AuthProvider';

// ── Dates ──────────────────────────────────────────────────────────────────
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function mondayOf(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
const DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
const fmtDay = (d: Date) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
const monthLabel = (y: number, m: number) => new Date(y, m - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

const TYPE_BADGE: Record<string, { l: string; c: string }> = {
  cold_call: { l: 'Cold call', c: 'text-hook-rose bg-hook-rose/10' },
  gtm: { l: 'GTM', c: 'text-st-ok bg-st-ok/10' },
  mixte: { l: 'Mixte', c: 'text-st-warn bg-st-warn/10' },
  '?': { l: '—', c: 'text-faint bg-raise' },
};
const CHAN_BADGE: Record<string, { l: string; c: string }> = {
  cold_call: { l: 'CC', c: 'text-hook-rose bg-hook-rose/10' },
  gtm: { l: 'GTM', c: 'text-st-ok bg-st-ok/10' },
};

// Agenda d'affectation : planification de capacité. On affecte des clients (et leurs jours)
// aux SDR ; l'onglet Capacité en déduit le remplissage de chaque SDR et de l'équipe →
// jours-SDR encore vendables. Tout dérive de prospecting_schedule (jours de prospection).
const AgendaPage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || '';
  const isManager = ['csm', 'admin', 'super_admin'].includes(role);
  const canPreview = role === 'super_admin' || role === 'admin'; // peut prévisualiser la vue SDR
  const [mode, setMode] = useState<'mgmt' | 'sdr'>(role === 'sdr' ? 'sdr' : 'mgmt');
  const sdrMode = role === 'sdr' || mode === 'sdr';
  const canEdit = isManager && !sdrMode;
  const [tab, setTab] = useState<'capacite' | 'planning'>('capacite');
  // Filtre client du planning — réglable depuis le tableau staffing (« planifier › »).
  const [planClientFilter, setPlanClientFilter] = useState<string | null>(null);

  // Identité SDR en vue SDR : le vrai SDR = soi ; en prévisualisation (super/admin) = un SDR choisi.
  const [selfSdrId, setSelfSdrId] = useState<string | null>(role === 'sdr' ? (profile?.user_id ?? null) : null);
  const [sdrOpts, setSdrOpts] = useState<SdrOption[]>([]);
  useEffect(() => {
    if (sdrMode && role !== 'sdr') getAgendaSdrs().then(o => { setSdrOpts(o); setSelfSdrId(p => p ?? o[0]?.id ?? null); }).catch(() => {});
  }, [sdrMode, role]);

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">{sdrMode ? 'Mon agenda' : "Agenda d'affectation"}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {canPreview && (
            <div className="flex gap-1 bg-raise rounded-lg p-0.5">
              {([['mgmt', 'Management'], ['sdr', 'Vue SDR']] as const).map(([v, l]) => (
                <button key={v} onClick={() => setMode(v)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md ${mode === v ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{l}</button>
              ))}
            </div>
          )}
          {sdrMode && role !== 'sdr' && (
            <select value={selfSdrId ?? ''} onChange={e => setSelfSdrId(e.target.value)} className="rounded-md border border-line-2 bg-panel px-2 py-1 text-[12px]">
              {sdrOpts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          {!sdrMode && canEdit && (
            <div className="flex gap-1 bg-raise rounded-lg p-0.5">
              {([['capacite', 'Capacité'], ['planning', 'Planning semaine']] as const).map(([v, l]) => (
                <button key={v} onClick={() => setTab(v)} className={`text-[12px] font-semibold px-3 py-1 rounded-md ${tab === v ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{l}</button>
              ))}
            </div>
          )}
        </div>
      </div>
      {tab === 'capacite' && canEdit
        ? <p className="text-dim text-[13px] mb-4">Combien de jours chaque SDR est-il rempli, et combien de jours-SDR peut-on encore vendre ce mois-ci.</p>
        : <p className="text-dim text-[13px] mb-4">{canEdit
          ? <>Affecte les clients aux SDR jour par jour. <span className="text-st-ok">GTM = continu</span>, <span className="text-hook-rose">cold call = jours mixés</span>.</>
          : <>Ton planning et celui de l'équipe. Clique un jour pour <b>demander du télétravail</b>. <span className="text-hook-rose">Cold call</span> · <span className="text-st-ok">GTM</span> · <span className="text-st-warn">absence</span>.</>}</p>}
      {tab === 'capacite' && canEdit
        ? <CapacityView canEdit={canEdit} onPlanClient={id => { setPlanClientFilter(id); setTab('planning'); }} />
        : <WeekPlanner canEdit={canEdit} sdrMode={sdrMode} selfSdrId={selfSdrId} clientFilter={planClientFilter} onClientFilter={setPlanClientFilter} />}
    </div>
  );
};

// ── Onglet Capacité ────────────────────────────────────────────────────────
const CapacityView: React.FC<{ canEdit: boolean; onPlanClient: (clientId: string) => void }> = ({ canEdit, onPlanClient }) => {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [board, setBoard] = useState<CapacityBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setBoard(await getCapacityBoard(ym.y, ym.m)); } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [ym.y, ym.m]);
  useEffect(() => { load(); }, [load]);

  const shift = (d: number) => setYm(p => { const nm = p.m + d; return { y: p.y + Math.floor((nm - 1) / 12), m: ((nm - 1 + 12) % 12) + 1 }; });
  const saveCap = async (sdrId: string) => {
    const v = parseFloat(editVal.replace(',', '.'));
    setEditing(null);
    if (!isNaN(v) && v >= 0) { await setSdrCapacity(sdrId, ym.y, ym.m, v).catch(() => {}); load(); }
  };

  const GTM_TARGET = 3; // missions vendables par full stack ; la 4e = bonus/marge (cerveau §9.7)
  const renderSdrCard = (s: SdrCapacityRow, team: string) => {
    const isGtm = team === 'gtm';
    // GTM : capacité = 3 missions (clients suivis). Pack : capacité en jours (jours GTM exclus, §9.5).
    const used = isGtm ? s.missions : s.assignedDays;
    const cap = isGtm ? GTM_TARGET : s.capacityDays;
    const bonus = isGtm && used > GTM_TARGET;       // 4e client GTM = marge
    const over = !isGtm && used > cap;              // surcharge = cold call uniquement
    const pct = cap ? Math.round((used / cap) * 100) : 0;
    const bar = over ? 'bg-st-bad' : (bonus || pct >= 90) ? 'bg-st-ok' : pct >= 50 ? 'bg-st-warn' : 'bg-line-2';
    const b = TYPE_BADGE[s.type] || TYPE_BADGE['?'];
    return (
      <div key={s.sdrId} className="bg-panel border border-line rounded-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div><div className="text-[13.5px] font-semibold">{s.name}</div><span className={`inline-block mt-0.5 font-mono text-[9px] px-1.5 py-0.5 rounded ${b.c}`}>{b.l}</span></div>
          <div className="text-right">
            {isGtm ? (
              <div className={`font-mono text-[15px] font-bold tabular-nums ${bonus ? 'text-st-ok' : ''}`}>{used}<span className="text-faint">/{GTM_TARGET}</span></div>
            ) : editing === s.sdrId && canEdit ? (
              <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)} onBlur={() => saveCap(s.sdrId)} onKeyDown={e => e.key === 'Enter' && saveCap(s.sdrId)}
                className="w-14 text-right rounded border border-hook-rose bg-panel px-1 py-0.5 text-[13px] font-mono" />
            ) : canEdit ? (
              <button onClick={() => { setEditing(s.sdrId); setEditVal(String(s.capacityDays)); }} className="font-mono text-[15px] font-bold tabular-nums hover:text-hook-rose" title="Modifier la capacité">
                {s.assignedDays}<span className="text-faint">/{s.capacityDays}</span>
              </button>
            ) : (
              <div className="font-mono text-[15px] font-bold tabular-nums">{s.assignedDays}<span className="text-faint">/{s.capacityDays}</span></div>
            )}
            <div className="font-mono text-[8.5px] uppercase tracking-widest text-faint">{isGtm ? 'clients simultanés' : `jours ${s.capacityIsDefault ? '(ouvrés)' : '(réglé)'}`}</div>
          </div>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-raise overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
        <div className="mt-1 flex items-center justify-between font-mono text-[10px]">
          <span className={over ? 'text-st-bad' : (bonus || pct >= 90) ? 'text-st-ok' : 'text-dim'}>{isGtm ? (bonus ? `${used} clients · +${used - GTM_TARGET} bonus (marge)` : `${used} client${used > 1 ? 's' : ''}`) : `${pct}%${over ? ' · surchargé' : ''}`}</span>
          {!isGtm && <span className="text-faint">{s.assignedDays}j prestés{s.absenceDays > 0 ? ` · ${s.absenceDays}j abs.` : ''}</span>}
        </div>
        {s.clients.length > 0 && (
          <ul className="mt-2.5 space-y-1 border-t border-line pt-2">
            {s.clients.map(c => (
              <li key={c.clientId} className="flex items-center gap-1.5 text-[11.5px]">
                {c.channel && <span className={`font-mono text-[8px] px-1 rounded ${CHAN_BADGE[c.channel]?.c}`}>{CHAN_BADGE[c.channel]?.l}</span>}
                <span className="truncate">{c.clientName}</span>
                <span className="ml-auto font-mono text-dim tabular-nums">{c.days}j</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const t = board?.team;
  return (
    <>
      <div className="flex items-center gap-1.5 mb-4">
        <button onClick={() => shift(-1)} className="w-7 h-7 rounded-md border border-line-2 text-dim hover:border-ink">‹</button>
        <span className="font-mono text-[12px] text-dim w-40 text-center capitalize">{monthLabel(ym.y, ym.m)}</span>
        <button onClick={() => shift(1)} className="w-7 h-7 rounded-md border border-line-2 text-dim hover:border-ink">›</button>
        <button onClick={() => setYm({ y: now.getFullYear(), m: now.getMonth() + 1 })} className="ml-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-md border border-line-2 text-dim hover:border-ink">Ce mois</button>
      </div>

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}
      {loading || !t ? <SkeletonCards count={6} /> : (
        <>
          {/* Bandeau capacité équipe — règle du cerveau (§9.6, Kevin 04/09) : le pack se compte en
              JOURNÉES, le GTM en MISSIONS (3/full stack), jamais convertis. Globale = moyenne des
              deux taux pondérée par le nombre de producteurs. */}
          <div className="bg-hook-dark text-white rounded-card p-5 mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {/* Pack : jours */}
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[28px] font-bold font-mono leading-none">{t.pack.assignedDays}<span className="text-neutral-500 text-[18px]">/{t.pack.capacityDays}</span></span>
                  <span className="font-mono text-[13px] text-neutral-300">{Math.round(t.pack.rate * 100)}%</span>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-400 mt-1">Pack — journées ({t.pack.producers} SDR)</div>
                <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className={`h-full ${t.pack.freeDays < 0 ? 'bg-red-400' : 'bg-emerald-400'}`} style={{ width: `${Math.min(100, t.pack.rate * 100)}%` }} /></div>
                <p className="mt-1.5 text-[11px] text-neutral-300">
                  {t.pack.freeDays > 0 ? <><b className="text-white">{t.pack.freeDays} j</b> encore vendables</>
                    : t.pack.freeDays < 0 ? <span className="text-red-300">⚠ sur-vendu de {-t.pack.freeDays} j</span>
                      : 'complet'}
                </p>
              </div>
              {/* GTM : missions */}
              <div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-[28px] font-bold font-mono leading-none ${t.gtm.rate > 1 ? 'text-emerald-300' : ''}`}>{t.gtm.missions}<span className="text-neutral-500 text-[18px]">/{t.gtm.missionCap}</span></span>
                  <span className="font-mono text-[13px] text-neutral-300">{Math.round(t.gtm.rate * 100)}%</span>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-400 mt-1">GTM — missions ({t.gtm.producers} full stack × 3)</div>
                <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-emerald-400" style={{ width: `${Math.min(100, t.gtm.rate * 100)}%` }} /></div>
                <p className="mt-1.5 text-[11px] text-neutral-300">
                  {t.gtm.freeMissions > 0 ? <><b className="text-white">{t.gtm.freeMissions} mission{t.gtm.freeMissions > 1 ? 's' : ''}</b> encore vendable{t.gtm.freeMissions > 1 ? 's' : ''}</>
                    : t.gtm.freeMissions < 0 ? <span className="text-emerald-300">+{-t.gtm.freeMissions} au-delà du plafond (marge, assumé)</span>
                      : 'complet (9 = 100 %)'}
                </p>
              </div>
              {/* Globale : moyenne pondérée, sans conversion */}
              <div>
                <div className="text-[28px] font-bold font-mono leading-none">{Math.round(t.globalRate * 100)}%</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-400 mt-1">Capacité globale</div>
                <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-white/70" style={{ width: `${Math.min(100, t.globalRate * 100)}%` }} /></div>
                <p className="mt-1.5 text-[11px] text-neutral-400">Moyenne des deux taux pondérée par producteurs — jamais de conversion missions↔jours (cerveau §9.6).</p>
              </div>
            </div>
          </div>

          {/* Charge par SDR, groupée par équipe */}
          {([
            ['cold_call', 'Équipe Cold call', 'text-hook-rose'],
            ['gtm', 'Équipe GTM', 'text-st-ok'],
            ['?', 'Sans équipe (à assigner)', 'text-st-warn'],
          ] as const).map(([team, label, tone]) => {
            const list = board.sdrs.filter(s => (team === '?' ? (s.type !== 'cold_call' && s.type !== 'gtm') : s.type === team));
            if (!list.length) return null;
            return (
              <section key={team} className="mb-5">
                <h2 className={`font-mono text-[10px] uppercase tracking-widest mb-2 ${tone}`}>{label} · {list.length}
                  {team === '?' && <span className="text-faint normal-case tracking-normal"> — donne-leur une équipe dans « Équipe & rôles »</span>}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map(s => renderSdrCard(s, team))}
                </div>
              </section>
            );
          })}

          {/* Staffing par client : vendu vs planifié — CLIENTS AU JOUR uniquement.
              Les GTM ne se comptent jamais en jours (cerveau §9.6) → section missions à part. */}
          <h2 className="font-mono text-[10px] uppercase tracking-widest text-faint mb-2">Staffing par client (au jour) · jours vendus vs planifiés</h2>
          <div className="bg-panel border border-line rounded-card overflow-x-auto">
            <table className="w-full border-collapse min-w-[560px]">
              <thead><tr className="border-b border-line-2 font-mono text-[9.5px] uppercase tracking-widest text-faint">
                <th className="text-left py-2 px-3 font-semibold">Client</th>
                <th className="text-left py-2 px-2 font-semibold">Canal</th>
                <th className="text-right py-2 px-2 font-semibold">Vendu</th>
                <th className="text-right py-2 px-2 font-semibold">Planifié</th>
                <th className="text-right py-2 px-2 font-semibold">Écart</th>
                <th className="text-left py-2 px-3 font-semibold w-40">Remplissage</th>
                <th className="py-2 px-2 w-24"></th>
              </tr></thead>
              <tbody className="divide-y divide-line">
                {board.clients.filter(c => c.channel !== 'gtm').map(c => {
                  const prog = c.sold ? Math.round((c.planned / c.sold) * 100) : null;
                  const gap = (c.sold ?? 0) - c.planned;
                  const tone = prog == null ? 'text-faint' : prog < 80 ? 'text-st-bad' : prog < 95 ? 'text-st-warn' : 'text-st-ok';
                  const barTone = prog == null ? 'bg-line-2' : prog < 80 ? 'bg-st-bad' : prog < 95 ? 'bg-st-warn' : 'bg-st-ok';
                  return (
                    <tr key={c.clientId} className="hover:bg-panel-2">
                      <td className="py-2 px-3 text-[12.5px] font-medium truncate max-w-[180px]">{c.name}</td>
                      <td className="py-2 px-2">{c.channel && <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded ${CHAN_BADGE[c.channel]?.c}`}>{CHAN_BADGE[c.channel]?.l}</span>}</td>
                      <td className="py-2 px-2 text-right font-mono text-[12px] tabular-nums text-dim">{c.sold ?? '—'}</td>
                      <td className="py-2 px-2 text-right font-mono text-[12px] tabular-nums">{c.planned}</td>
                      <td className={`py-2 px-2 text-right font-mono text-[12px] tabular-nums ${gap > 0 ? 'text-st-bad' : 'text-dim'}`}>{c.sold == null ? '—' : gap > 0 ? `-${gap}` : gap < 0 ? `+${-gap}` : '0'}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-raise overflow-hidden"><div className={`h-full ${barTone}`} style={{ width: `${Math.min(100, prog ?? 0)}%` }} /></div>
                          <span className={`font-mono text-[10px] w-9 text-right ${tone}`}>{prog == null ? '—' : `${prog}%`}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button onClick={() => onPlanClient(c.clientId)} title="Ouvrir le planning filtré sur ce client"
                          className={`font-mono text-[10px] px-2 py-1 rounded border transition ${gap > 0 ? 'border-hook-rose/40 text-hook-rose hover:bg-hook-rose hover:text-white' : 'border-line-2 text-dim hover:border-ink'}`}>
                          {gap > 0 ? '＋ planifier' : 'voir les jours'} ›
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 font-mono text-[10px] text-faint">Écart = jours vendus − planifiés (positif = client sous-staffé). Capacité SDR par défaut = jours ouvrés du mois − absences ; clique le ratio pour la régler.</p>

          {/* Missions GTM : en places (qui les porte), jamais en jours. */}
          {(() => {
            const carriers = new Map<string, string[]>(); // client GTM → SDR full stack qui le portent
            for (const s of board.sdrs.filter(x => x.type === 'gtm'))
              for (const cl of s.clients.filter(x => x.channel === 'gtm')) {
                if (!carriers.has(cl.clientId)) carriers.set(cl.clientId, []);
                carriers.get(cl.clientId)!.push(s.name.split(' ')[0]);
              }
            const gtmClients = board.clients.filter(c => c.channel === 'gtm');
            if (!gtmClients.length) return null;
            return (
              <>
                <h2 className="font-mono text-[10px] uppercase tracking-widest text-st-ok mt-5 mb-2">Missions GTM · en places (continu, jamais en jours)</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {gtmClients.map(c => {
                    const who = carriers.get(c.clientId) ?? [];
                    const vendues = c.gtmMissions; // missions vendues au contrat (ligne GTM)
                    const short = vendues != null && who.length < vendues;
                    return (
                      <div key={c.clientId} className="bg-panel border border-line rounded-card px-3 py-2.5 flex items-center gap-2">
                        <span className="text-[12.5px] font-semibold truncate">{c.name}</span>
                        <span className={`ml-auto font-mono text-[10px] px-1.5 py-0.5 rounded shrink-0 ${!who.length ? 'bg-st-bad/10 text-st-bad' : short ? 'bg-st-warn/10 text-st-warn' : 'bg-st-ok/10 text-st-ok'}`}>
                          {vendues != null ? `${who.length}/${vendues}` : `${who.length} place${who.length > 1 ? 's' : ''}`}
                          {who.length ? ` · ${who.join(', ')}` : ' · aucun SDR'}
                          {short ? ` · manque ${vendues! - who.length}` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 font-mono text-[10px] text-faint">Une place = un SDR full stack qui suit le client ce mois (d'après le planning). Un client peut prendre plusieurs places (ex. Velatir ×3).</p>
              </>
            );
          })()}
        </>
      )}
    </>
  );
};

// ── Onglet Planning semaine (affectation jour par jour) ────────────────────
// Clés internes alignées sur la contrainte prospecting_schedule_work_location_check.
const LOCATIONS = [{ v: 'office', l: 'Bureau' }, { v: 'remote', l: 'Télétravail' }, { v: 'other', l: 'Visio / autre' }];
const LOC_TAG: Record<string, string> = { remote: 'TT', other: 'VIS', office: '' };
// Types de journées non-prospection (alignés sur les valeurs déjà en base). 'autre' = texte libre.
const DAY_TYPES = [
  { v: 'congé', l: 'Congé', icon: '🌴' },
  { v: 'malade', l: 'Maladie', icon: '🤒' },
  { v: 'Ecole', l: 'École', icon: '🎓' },
  { v: 'formation', l: 'Formation', icon: '📚' },
  { v: 'encadrement', l: 'Encadrement', icon: '🧭' },
  { v: 'férié', l: 'Férié', icon: '📅' },
  { v: 'Jour Off', l: 'Jour Off', icon: '💤' },
  { v: 'autre', l: 'Autre…', icon: '•' },
];
const dayTypeIcon = (dt: string) => DAY_TYPES.find(t => t.v === dt)?.icon ?? '•';
// Code couleur par canal du client.
const CH_CHIP: Record<string, string> = {
  gtm: 'bg-st-ok/10 border-st-ok/40 text-st-ok',
  cold_call: 'bg-hook-rose/10 border-hook-rose/40 text-hook-rose',
};
const CH_DOT: Record<string, string> = { gtm: 'bg-st-ok', cold_call: 'bg-hook-rose' };

const WeekPlanner: React.FC<{ canEdit: boolean; sdrMode?: boolean; selfSdrId?: string | null; clientFilter?: string | null; onClientFilter?: (id: string | null) => void }> = ({ canEdit, sdrMode = false, selfSdrId = null, clientFilter = null, onClientFilter }) => {
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [range, setRange] = useState<'week' | 'month'>('week');
  const [sdrsRaw, setSdrsRaw] = useState<SdrOption[]>([]);
  const [clients, setClients] = useState<AssignableClient[]>([]);
  const [cells, setCells] = useState<ScheduleCell[]>([]);
  const [tt, setTt] = useState<TtRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [assign, setAssign] = useState<{ sdr: SdrOption; date: string } | null>(null);
  const [dayDetail, setDayDetail] = useState<{ sdr: SdrOption; date: string } | null>(null);
  const compact = range === 'month';
  const anchorMonth = anchor.getMonth();
  // Vue SDR : le SDR concerné en tête, les autres dessous.
  const sdrs = useMemo(() => selfSdrId ? [...sdrsRaw.filter(s => s.id === selfSdrId), ...sdrsRaw.filter(s => s.id !== selfSdrId)] : sdrsRaw, [sdrsRaw, selfSdrId]);
  const ttByKey = useMemo(() => new Map(tt.map(r => [`${r.sdrId}|${r.date}`, r.status])), [tt]);

  // Jours ouvrés (lun-ven). Semaine = 5 j ; Mois = mois calendaire complet (du lundi de la
  // 1re semaine au vendredi de la dernière — donc septembre va jusqu'au 2 oct).
  const days = useMemo(() => {
    if (!compact) {
      const start = mondayOf(anchor);
      return Array.from({ length: 5 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
    }
    const y = anchor.getFullYear(), m = anchor.getMonth();
    const start = mondayOf(new Date(y, m, 1));
    const endMon = mondayOf(new Date(y, m + 1, 0));
    const endFri = new Date(endMon); endFri.setDate(endFri.getDate() + 4);
    const out: Date[] = []; const cur = new Date(start);
    while (cur <= endFri) { const wd = cur.getDay(); if (wd >= 1 && wd <= 5) out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    return out;
  }, [anchor, compact]);
  const fromISO = iso(days[0]);
  const toISO = iso(days[days.length - 1]);
  const channelByClient = useMemo(() => new Map(clients.map(c => [c.id, c.channel])), [clients]);

  const loadTt = useCallback(async () => {
    if (!sdrMode && !canEdit) { setTt([]); return; }
    try { setTt(await getTtRequests(sdrMode && selfSdrId ? selfSdrId : undefined)); } catch { /* */ }
  }, [sdrMode, canEdit, selfSdrId]);
  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [s, c, sched] = await Promise.all([getAgendaSdrs(), getAssignableClients(), getWeekSchedule(fromISO, toISO)]);
      setSdrsRaw(s); setClients(c); setCells(sched);
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
    loadTt();
  }, [fromISO, toISO, loadTt]);
  useEffect(() => { load(); }, [load]);
  const refresh = useCallback(async () => { try { setCells(await getWeekSchedule(fromISO, toISO)); } catch { /* */ } }, [fromISO, toISO]);
  const requestTt = async (date: string) => { if (selfSdrId) { await requestTeletravail(selfSdrId, date).catch(() => {}); loadTt(); } };
  const decideTt = async (id: string, status: 'approved' | 'refused') => { await decideTtRequest(id, status).catch(() => {}); loadTt(); refresh(); };

  const cellFor = (sdrId: string, dISO: string) => cells.filter(c => c.sdrId === sdrId && c.date === dISO);
  const remove = async (id: string) => { setCells(cs => cs.filter(c => c.id !== id)); await removeScheduleDay(id).catch(() => {}); };
  const shift = (d: number) => { const x = new Date(anchor); if (compact) x.setMonth(x.getMonth() + d); else x.setDate(x.getDate() + d * 7); setAnchor(x); };
  const rangeISO = days.map(iso);
  const isAbsence = (a: ScheduleCell) => isAbsenceDayType(a.dayType) || (a.dayType !== 'prospection' && !a.clientId);

  // Visibilité « non affecté » sur la portée : clients cold call sans jour + SDR avec jours libres.
  const prospByClient = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cells) if (c.clientId && c.dayType === 'prospection') m.set(c.clientId, (m.get(c.clientId) || 0) + 1);
    return m;
  }, [cells]);
  const coldNoDay = clients.filter(c => c.channel !== 'gtm' && !prospByClient.get(c.id));
  const freeBySdr = sdrs.map(s => {
    const busy = new Set(cells.filter(c => c.sdrId === s.id && rangeISO.includes(c.date)).map(c => c.date));
    return { sdr: s, free: rangeISO.filter(d => !busy.has(d)).length };
  }).filter(x => x.free > 0);
  const periodLabel = compact ? 'ce mois' : 'cette semaine';
  const navLabel = compact ? monthLabel(anchor.getFullYear(), anchor.getMonth() + 1) : `${fmtDay(days[0])} – ${fmtDay(days[days.length - 1])}`;

  // Filtre client : compteur de jours planifiés sur la portée + client sélectionné.
  const filteredClient = clientFilter ? clients.find(c => c.id === clientFilter) ?? null : null;
  const filterDays = useMemo(() => clientFilter
    ? cells.filter(c => c.clientId === clientFilter && c.dayType === 'prospection' && rangeISO.includes(c.date)).length
    : 0, [cells, clientFilter, rangeISO]);
  // Avec un filtre : ne garder que les SDR concernés + ceux qui ont des jours libres (pour en ajouter).
  const shownSdrs = useMemo(() => {
    if (!clientFilter || !canEdit) return sdrs;
    const has = new Set(cells.filter(c => c.clientId === clientFilter).map(c => c.sdrId));
    return sdrs.filter(s => has.has(s.id) || rangeISO.some(d => !cells.some(c => c.sdrId === s.id && c.date === d)));
  }, [sdrs, cells, clientFilter, canEdit, rangeISO]);

  return (
    <>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        <button onClick={() => shift(-1)} className="w-7 h-7 rounded-md border border-line-2 text-dim hover:border-ink">‹</button>
        <span className="font-mono text-[12px] text-dim tabular-nums w-44 text-center capitalize">{navLabel}</span>
        <button onClick={() => shift(1)} className="w-7 h-7 rounded-md border border-line-2 text-dim hover:border-ink">›</button>
        <button onClick={() => setAnchor(new Date())} className="ml-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-md border border-line-2 text-dim hover:border-ink">{compact ? 'Ce mois' : "Aujourd'hui"}</button>
        {onClientFilter && (
          <select value={clientFilter ?? ''} onChange={e => onClientFilter(e.target.value || null)}
            className={`ml-1 rounded-md border px-2 py-1 text-[12px] max-w-[180px] ${clientFilter ? 'border-hook-rose text-hook-rose font-semibold bg-hook-rose/5' : 'border-line-2 bg-panel text-dim'}`}>
            <option value="">Tous les clients</option>
            {[...clients].sort((a, b) => a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <div className="ml-auto flex gap-1 bg-raise rounded-lg p-0.5">
          {([['week', 'Semaine'], ['month', 'Mois']] as const).map(([v, l]) => (
            <button key={v} onClick={() => setRange(v)} className={`text-[11.5px] font-semibold px-3 py-1 rounded-md ${range === v ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{l}</button>
          ))}
        </div>
      </div>
      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}

      {/* Bandeau du filtre client : combien de jours planifiés vs vendus, d'un coup d'œil. */}
      {filteredClient && (
        <div className="bg-hook-rose/5 border border-hook-rose/30 rounded-card p-3 mb-3 flex items-center gap-3 flex-wrap">
          <span className="text-[13px] font-bold">{filteredClient.name}</span>
          <span className="font-mono text-[12px]">
            <b className={filteredClient.sold != null && filterDays < filteredClient.sold ? 'text-st-bad' : 'text-st-ok'}>{filterDays} j</b>
            <span className="text-dim"> planifiés {periodLabel}{filteredClient.sold != null && <> · vendu {filteredClient.sold} j/mois</>}</span>
          </span>
          {filteredClient.sold != null && compact && filterDays < filteredClient.sold && (
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-st-bad/10 text-st-bad">manque {filteredClient.sold - filterDays} j — clique « + » sur un jour libre</span>
          )}
          <button onClick={() => onClientFilter?.(null)} className="ml-auto font-mono text-[11px] text-dim hover:text-ink">× retirer le filtre</button>
        </div>
      )}

      {/* Notifications : demandes de télétravail en attente (manager) */}
      {canEdit && tt.some(r => r.status === 'pending') && (
        <div className="bg-st-warn/5 border border-st-warn/30 rounded-card p-3 mb-3">
          <div className="font-mono text-[9px] uppercase tracking-widest text-st-warn mb-2">🏠 Demandes de télétravail · {tt.filter(r => r.status === 'pending').length}</div>
          <div className="space-y-1.5">
            {tt.filter(r => r.status === 'pending').map(r => (
              <div key={r.id} className="flex items-center gap-2 text-[12.5px]">
                <span className="font-semibold">{r.sdrName}</span>
                <span className="text-dim capitalize">{new Date(r.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' })}</span>
                <button onClick={() => decideTt(r.id, 'approved')} className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-md bg-st-ok text-white hover:opacity-90">Approuver</button>
                <button onClick={() => decideTt(r.id, 'refused')} className="text-[11px] font-semibold px-2 py-0.5 rounded-md border border-line-2 text-dim hover:border-st-bad hover:text-st-bad">Refuser</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Panneau « à placer / non affecté » (management uniquement) */}
      {canEdit && !loading && (coldNoDay.length > 0 || freeBySdr.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="bg-hook-rose/5 border border-hook-rose/25 rounded-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-hook-rose mb-1.5">Clients cold call sans créneau {periodLabel} · {coldNoDay.length}</div>
            {coldNoDay.length === 0 ? <p className="text-[11.5px] text-dim">Tous les clients cold call ont au moins un jour. 👍</p> : (
              <div className="flex flex-wrap gap-1">{coldNoDay.map(c => <span key={c.id} className="text-[11px] bg-panel border border-line-2 rounded px-1.5 py-0.5">{c.name}</span>)}</div>
            )}
          </div>
          <div className="bg-panel border border-line rounded-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1.5">SDR avec des jours libres · {freeBySdr.reduce((t, x) => t + x.free, 0)} j</div>
            {freeBySdr.length === 0 ? <p className="text-[11.5px] text-dim">Tout le monde est plein.</p> : (
              <div className="flex flex-wrap gap-1">{freeBySdr.map(x => <span key={x.sdr.id} className="text-[11px] bg-raise rounded px-1.5 py-0.5">{x.sdr.name} <span className="font-mono text-faint">{x.free}j</span></span>)}</div>
            )}
          </div>
        </div>
      )}

      {/* Légende couleurs */}
      <div className="flex items-center gap-3 mb-2 font-mono text-[10px] text-faint">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-hook-rose" />Cold call</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-st-ok" />GTM</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-st-warn" />Absence</span>
      </div>

      {loading ? <SkeletonRows count={8} /> : (
        <div className="bg-panel border border-line rounded-card overflow-x-auto">
          <table className="w-full border-collapse" style={{ minWidth: compact ? 1100 : 880 }}>
            <thead><tr className="border-b border-line-2">
              <th className="text-left py-2.5 px-3 font-mono text-[9.5px] uppercase tracking-widest text-faint sticky left-0 bg-panel w-40">SDR</th>
              {days.map((d, i) => (
                <th key={i} className={`py-2 px-1.5 font-mono text-[9px] uppercase tracking-widest text-faint ${compact ? 'text-center border-l border-line' : 'text-left'} ${compact && i % 5 === 0 && i > 0 ? 'border-l-2 border-line-2' : ''} ${compact && d.getMonth() !== anchorMonth ? 'opacity-40' : ''}`}>
                  {compact ? <span className="text-[9px]">{['L', 'M', 'M', 'J', 'V'][i % 5]}{d.getDate()}</span> : <>{DAY_LABELS[i]}<span className="block text-[9px] text-line-2 normal-case tracking-normal">{fmtDay(d)}</span></>}
                </th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-line">
              {shownSdrs.length === 0 ? (
                <tr><td colSpan={days.length + 1} className="py-8"><EmptyState title="Aucun SDR" hint="Assigne une équipe dans « Équipe & rôles »." /></td></tr>
              ) : shownSdrs.map(sdr => {
                const b = TYPE_BADGE[sdr.type] || TYPE_BADGE['?'];
                const isSelf = selfSdrId === sdr.id;
                return (
                  <tr key={sdr.id} className={`align-top ${isSelf ? 'bg-hook-rose/5' : ''}`}>
                    <td className={`py-2 px-3 sticky left-0 ${isSelf ? 'bg-[#fff5f7]' : 'bg-panel'}`}>
                      <div className="text-[13px] font-semibold whitespace-nowrap flex items-center gap-1.5">{sdr.name}{isSelf && <span className="font-mono text-[8px] text-hook-rose bg-hook-rose/10 rounded px-1 not-italic">MOI</span>}</div>
                      <span className={`inline-block mt-0.5 font-mono text-[9px] px-1.5 py-0.5 rounded ${b.c}`}>{b.l}</span>
                    </td>
                    {days.map((d, i) => {
                      const dISO = iso(d);
                      const items = cellFor(sdr.id, dISO);
                      const sep = compact && i % 5 === 0 && i > 0 ? 'border-l-2 border-line-2' : '';
                      if (compact) {
                        const outMonth = d.getMonth() !== anchorMonth;
                        // Case pleine = bloc(s) de couleur (par canal / absence) ; case vide = hachures.
                        const clientCount = items.filter(a => !isAbsence(a)).length;
                        const title = items.map(a => isAbsence(a) ? a.dayType || 'absence' : a.clientName || '—').join(' · ') || 'libre';
                        const inner = items.length === 0
                          ? <div className="w-full h-full" style={{ background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(130,130,130,0.13) 3px, rgba(130,130,130,0.13) 4.5px)' }} />
                          : <div className="flex w-full h-full gap-px">{items.map(a => {
                              const cls = isAbsence(a) ? 'bg-st-warn' : (CH_DOT[channelByClient.get(a.clientId || '') || ''] || 'bg-ink');
                              const dim = clientFilter && a.clientId !== clientFilter ? 'opacity-20' : '';
                              return <div key={a.id} className={`flex-1 ${cls} ${dim}`} />;
                            })}</div>;
                        const cell = (
                          <div className={`relative h-6 w-full overflow-hidden rounded-[3px] ring-1 ring-inset ring-line ${outMonth ? 'opacity-30' : ''}`} title={title}>
                            {inner}
                            {clientCount > 1 && <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white drop-shadow">×{clientCount}</span>}
                          </div>
                        );
                        return (
                          <td key={i} className={`p-[3px] border-l border-line ${sep} ${outMonth ? 'bg-line/5' : ''}`}>
                            <button onClick={() => setDayDetail({ sdr, date: dISO })} className="block w-full hover:opacity-80">{cell}</button>
                          </td>
                        );
                      }
                      const ttHere = ttByKey.get(`${sdr.id}|${dISO}`);
                      const chips = (
                        <div className="flex flex-col gap-1">
                          {items.map(a => {
                            if (isAbsence(a)) return (
                              <span key={a.id} className="group inline-flex items-center gap-1 bg-st-warn/10 border border-st-warn/30 text-st-warn rounded px-1.5 py-0.5 text-[11.5px]">
                                <span>{dayTypeIcon(a.dayType || '')}</span>
                                <span className="truncate max-w-[110px]">{a.dayType || 'Absence'}</span>
                                {canEdit && <button onClick={() => remove(a.id)} className="ml-auto opacity-0 group-hover:opacity-100 hover:text-st-bad" aria-label="Retirer">×</button>}
                              </span>
                            );
                            const cls = CH_CHIP[channelByClient.get(a.clientId || '') || ''] || 'bg-raise border-line-2 text-ink';
                            const dim = clientFilter ? (a.clientId === clientFilter ? 'ring-1 ring-hook-rose' : 'opacity-30') : '';
                            return (
                              <span key={a.id} className={`group inline-flex items-center gap-1 border rounded px-1.5 py-0.5 text-[11.5px] ${cls} ${dim}`}>
                                <span className="truncate max-w-[120px]">{a.clientName || '—'}</span>
                                {a.workLocation && LOC_TAG[a.workLocation] && <span className="font-mono text-[8px] opacity-70 uppercase">{LOC_TAG[a.workLocation]}</span>}
                                {canEdit && <button onClick={() => remove(a.id)} className="ml-auto opacity-0 group-hover:opacity-100 hover:text-st-bad" aria-label="Retirer">×</button>}
                              </span>
                            );
                          })}
                          {ttHere && <span className={`inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded ${TT_LABEL[ttHere].c}`}>🏠 TT {ttHere === 'pending' ? '?' : ttHere === 'approved' ? '✓' : '✗'}</span>}
                          {canEdit && <button onClick={() => setAssign({ sdr, date: dISO })} className="text-[11px] text-faint hover:text-hook-rose text-left px-1.5 py-0.5 rounded border border-dashed border-line-2 hover:border-hook-rose">+</button>}
                          {!canEdit && items.length === 0 && !ttHere && <span className="text-[11px] text-line-2">—</span>}
                        </div>
                      );
                      return (
                        <td key={i} className="py-2 px-2 min-w-[150px]">
                          {sdrMode ? <button onClick={() => setDayDetail({ sdr, date: dISO })} className="block w-full text-left rounded hover:bg-panel-2 px-0.5 -mx-0.5">{chips}</button> : chips}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {dayDetail && (
        <DayDetailModal
          sdr={dayDetail.sdr} date={dayDetail.date}
          items={cellFor(dayDetail.sdr.id, dayDetail.date)}
          channelByClient={channelByClient} canEdit={canEdit}
          ttStatus={(ttByKey.get(`${dayDetail.sdr.id}|${dayDetail.date}`) as any) || null}
          onRequestTt={sdrMode && dayDetail.sdr.id === selfSdrId ? async () => { const d = dayDetail.date; setDayDetail(null); await requestTt(d); } : undefined}
          onClose={() => setDayDetail(null)}
          onRemove={async id => { await remove(id); }}
          onAdd={() => { const dd = dayDetail; setDayDetail(null); if (dd) setAssign({ sdr: dd.sdr, date: dd.date }); }}
        />
      )}
      {canEdit && assign && <AssignModal sdr={assign.sdr} date={assign.date} clients={clients} defaultClientId={clientFilter} onClose={() => setAssign(null)} onDone={() => { setAssign(null); refresh(); }} />}
    </>
  );
};

// ── Détail d'une journée (clic depuis la vue mois) ─────────────────────────
const TT_LABEL: Record<string, { l: string; c: string }> = {
  pending: { l: 'Télétravail demandé — en attente', c: 'text-st-warn bg-st-warn/10 border-st-warn/30' },
  approved: { l: 'Télétravail approuvé', c: 'text-st-ok bg-st-ok/10 border-st-ok/30' },
  refused: { l: 'Télétravail refusé', c: 'text-st-bad bg-st-bad/10 border-st-bad/30' },
};
const DayDetailModal: React.FC<{
  sdr: SdrOption; date: string; items: ScheduleCell[]; channelByClient: Map<string, string | null>;
  canEdit: boolean; ttStatus?: 'pending' | 'approved' | 'refused' | null; onRequestTt?: () => void;
  onClose: () => void; onRemove: (id: string) => void | Promise<void>; onAdd: () => void;
}> = ({ sdr, date, items, channelByClient, canEdit, ttStatus, onRequestTt, onClose, onRemove, onAdd }) => {
  const dLabel = new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' });
  const isAbs = (a: ScheduleCell) => isAbsenceDayType(a.dayType) || (a.dayType !== 'prospection' && !a.clientId);
  const clientItems = items.filter(a => !isAbs(a));
  const absItems = items.filter(isAbs);
  const b = TYPE_BADGE[sdr.type] || TYPE_BADGE['?'];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-panel border border-line rounded-card w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-[15px] font-bold">{sdr.name}</h3>
          <button onClick={onClose} className="text-faint hover:text-ink text-[18px] leading-none">×</button>
        </div>
        <p className="text-dim text-[12.5px] mb-3 capitalize">{dLabel} · <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded ${b.c}`}>{b.l}</span></p>

        {clientItems.length > 0 && (
          <div className="mb-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1.5">{clientItems.length} client{clientItems.length > 1 ? 's' : ''} ce jour</div>
            <div className="space-y-1.5">
              {clientItems.map(a => {
                const cls = CH_CHIP[channelByClient.get(a.clientId || '') || ''] || 'bg-raise border-line-2 text-ink';
                return (
                  <div key={a.id} className={`flex items-center gap-2 border rounded-md px-2 py-1.5 text-[12.5px] ${cls}`}>
                    <span className="truncate">{a.clientName || '—'}</span>
                    {a.workLocation && LOC_TAG[a.workLocation] && <span className="font-mono text-[8px] opacity-70 uppercase">{LOC_TAG[a.workLocation]}</span>}
                    {canEdit && <button onClick={() => onRemove(a.id)} className="ml-auto text-faint hover:text-st-bad">retirer</button>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {absItems.length > 0 && (
          <div className="mb-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1.5">Absence / autre</div>
            <div className="space-y-1.5">
              {absItems.map(a => (
                <div key={a.id} className="flex items-center gap-2 border border-st-warn/30 bg-st-warn/10 text-st-warn rounded-md px-2 py-1.5 text-[12.5px]">
                  <span>{dayTypeIcon(a.dayType || '')}</span><span className="truncate">{a.dayType || 'Absence'}</span>
                  {canEdit && <button onClick={() => onRemove(a.id)} className="ml-auto text-st-warn/70 hover:text-st-bad">retirer</button>}
                </div>
              ))}
            </div>
          </div>
        )}

        {items.length === 0 && <p className="text-dim text-[12.5px] mb-3 italic">Journée libre — rien de planifié.</p>}

        {ttStatus && <div className={`mb-3 text-[11.5px] font-semibold border rounded-md px-2 py-1.5 ${TT_LABEL[ttStatus].c}`}>🏠 {TT_LABEL[ttStatus].l}</div>}

        {canEdit ? (
          <button onClick={onAdd} className="w-full text-[12.5px] font-semibold px-3 py-2 rounded-md bg-hook-rose text-white hover:opacity-90">+ Ajouter un client / une absence</button>
        ) : onRequestTt ? (
          <button onClick={onRequestTt} disabled={ttStatus === 'pending'} className="w-full text-[12.5px] font-semibold px-3 py-2 rounded-md bg-hook-rose text-white hover:opacity-90 disabled:opacity-50">
            {ttStatus === 'pending' ? 'Demande déjà envoyée' : '🏠 Demander le télétravail ce jour'}
          </button>
        ) : (
          <button onClick={onClose} className="w-full text-[12.5px] font-semibold px-3 py-2 rounded-md border border-line-2 text-dim hover:border-ink">Fermer</button>
        )}
      </div>
    </div>
  );
};

const WEEKDAYS = [{ n: 1, l: 'Lun' }, { n: 2, l: 'Mar' }, { n: 3, l: 'Mer' }, { n: 4, l: 'Jeu' }, { n: 5, l: 'Ven' }];

const AssignModal: React.FC<{
  sdr: SdrOption; date: string; clients: AssignableClient[]; defaultClientId?: string | null;
  onClose: () => void; onDone: () => void;
}> = ({ sdr, date, clients, defaultClientId = null, onClose, onDone }) => {
  const startD = new Date(date + 'T00:00:00');
  const y = startD.getFullYear(); const mo = startD.getMonth();
  // Semaine (lun-ven) contenant le jour cliqué.
  const weekDates = useMemo(() => { const m = mondayOf(startD); return Array.from({ length: 5 }, (_, i) => { const d = new Date(m); d.setDate(d.getDate() + i); return iso(d); }); }, [date]);
  const [mode, setMode] = useState<'client' | 'absence'>('client');
  const [clientId, setClientId] = useState(defaultClientId ?? '');
  const [dayType, setDayType] = useState('congé');
  const [autreText, setAutreText] = useState('');
  const [weekdays, setWeekdays] = useState<number[]>([startD.getDay()]);
  const [scope, setScope] = useState<'week' | 'month'>('week');
  const [location, setLocation] = useState('office');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sdrCells, setSdrCells] = useState<ScheduleCell[] | null>(null); // planning du SDR sur le mois

  // La règle « 4 clients/jour » dépend du TYPE DU SDR (GTM), pas du canal du client :
  // un SDR GTM porte jusqu'à 4 clients simultanés, un SDR cold call = 1 client/jour.
  const GTM_MAX = 4;
  const isGtm = sdr.type === 'gtm';
  const gtmClients = clients.filter(c => c.channel === 'gtm');
  const ccClients = clients.filter(c => c.channel !== 'gtm');

  // Charge le planning du SDR sur le mois → sert à afficher la dispo et dédupliquer.
  useEffect(() => {
    const from = `${y}-${String(mo + 1).padStart(2, '0')}-01`;
    const to = `${y}-${String(mo + 1).padStart(2, '0')}-${String(new Date(y, mo + 1, 0).getDate()).padStart(2, '0')}`;
    getWeekSchedule(from, to).then(all => setSdrCells(all.filter(c => c.sdrId === sdr.id))).catch(() => setSdrCells([]));
  }, [sdr.id, y, mo]);

  const toggleWd = (n: number) => setWeekdays(ws => ws.includes(n) ? ws.filter(x => x !== n) : [...ws, n]);

  // Dates cibles selon la portée (semaine ou mois) et les jours de semaine choisis.
  const targetDates = useMemo(() => {
    const pool = scope === 'week'
      ? weekDates
      : Array.from({ length: new Date(y, mo + 1, 0).getDate() }, (_, i) => iso(new Date(y, mo, i + 1)));
    return pool.filter(d => weekdays.includes(new Date(d + 'T00:00:00').getDay()));
  }, [scope, weekDates, weekdays, y, mo]);

  // Statut de dispo du SDR par date cible.
  const byDate = useMemo(() => {
    const m = new Map<string, ScheduleCell[]>();
    for (const c of (sdrCells ?? [])) { if (!m.has(c.date)) m.set(c.date, []); m.get(c.date)!.push(c); }
    return m;
  }, [sdrCells]);
  const statusOf = (d: string): { s: 'free' | 'client' | 'busy' | 'absent'; label: string } => {
    const items = byDate.get(d) ?? [];
    const abs = items.find(i => isAbsenceDayType(i.dayType) || (i.dayType !== 'prospection' && !i.clientId));
    if (abs) return { s: 'absent', label: abs.dayType || 'absent' };
    const cl = items.find(i => i.dayType === 'prospection' && i.clientId);
    if (cl) return cl.clientId === clientId ? { s: 'busy', label: 'déjà ce client' } : { s: 'client', label: cl.clientName || 'autre client' };
    return { s: 'free', label: 'libre' };
  };
  // Nb de clients distincts déjà posés ce jour-là (plafond GTM = 4 clients/jour).
  const clientsOn = (d: string) => new Set((byDate.get(d) ?? []).filter(i => i.dayType === 'prospection' && i.clientId).map(i => i.clientId)).size;
  const dayEligible = (d: string) => { const st = statusOf(d).s; if (st === 'free') return true; if (isGtm && st === 'client') return clientsOn(d) < GTM_MAX; return false; };

  const currentClients = useMemo(() => {
    const s = new Map<string, string>();
    for (const c of (sdrCells ?? [])) if (c.dayType === 'prospection' && c.clientId) s.set(c.clientId, c.clientName || '—');
    return [...s.values()];
  }, [sdrCells]);
  const freeThisMonth = useMemo(() => {
    const days = Array.from({ length: new Date(y, mo + 1, 0).getDate() }, (_, i) => new Date(y, mo, i + 1)).filter(d => d.getDay() >= 1 && d.getDay() <= 5);
    return days.filter(d => (byDate.get(iso(d)) ?? []).length === 0).length;
  }, [byDate, y, mo]);

  const submit = async () => {
    setError(null);
    if (!targetDates.length) { setError('Choisis au moins un jour.'); return; }
    if (mode === 'client') {
      if (!clientId) { setError('Choisis un client.'); return; }
      // On ne pose pas sur un jour d'absence ; on saute les jours déjà pris (dédup + plafond GTM).
      const eligible = targetDates.filter(dayEligible);
      if (!eligible.length) {
        const gtmFull = isGtm && targetDates.some(d => statusOf(d).s === 'client' && clientsOn(d) >= GTM_MAX);
        const ccTaken = !isGtm && targetDates.some(d => statusOf(d).s === 'client');
        setError(gtmFull ? `GTM = ${GTM_MAX} clients/jour max : ces jours sont déjà pleins.`
          : ccTaken ? 'Cold call = 1 client/jour : ces jours sont déjà pris.'
          : 'Aucun jour disponible (absences, déjà ce client, ou jours pleins).');
        return;
      }
      setSaving(true);
      try { await assignScheduleDays({ sdrId: sdr.id, clientId, dates: eligible, workLocation: location }); onDone(); }
      catch (e: any) { setError(e.message || 'Échec.'); setSaving(false); }
    } else {
      const dt = dayType === 'autre' ? autreText.trim() : dayType;
      if (!dt) { setError('Précise le type de journée.'); return; }
      setSaving(true);
      try {
        // Une journée = une seule chose : poser une absence libère le client (→ à replacer).
        const toRemove = targetDates.flatMap(d => (byDate.get(d) ?? []).filter(i => i.dayType !== dt).map(i => i.id));
        for (const id of toRemove) await removeScheduleDay(id);
        const dates = targetDates.filter(d => !(byDate.get(d) ?? []).some(i => i.dayType === dt));
        if (dates.length) await assignScheduleDays({ sdrId: sdr.id, clientId: null, dates, dayType: dt });
        onDone();
      } catch (e: any) { setError(e.message || 'Échec.'); setSaving(false); }
    }
  };

  const nEligible = mode === 'client'
    ? targetDates.filter(dayEligible).length
    : targetDates.filter(d => !(byDate.get(d) ?? []).some(i => i.dayType === (dayType === 'autre' ? autreText.trim() : dayType))).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-panel border border-line rounded-card w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-1"><h3 className="text-[15px] font-bold">Affecter — {sdr.name}</h3><button onClick={onClose} className="text-faint hover:text-ink text-[18px] leading-none">×</button></div>

        {/* Dispo du SDR */}
        <div className="bg-raise rounded-lg px-3 py-2 mb-3 text-[11.5px] text-dim">
          {sdrCells == null ? 'Chargement de la dispo…' : (
            <>
              <span className="font-semibold text-ink">{freeThisMonth} jours libres</span> ce mois-ci.
              {sdr.type === 'gtm'
                ? <> Porte <span className="font-semibold text-ink">{currentClients.length}/3</span> clients GTM (4e = bonus){currentClients.length ? ` : ${currentClients.join(', ')}` : ''}.</>
                : currentClients.length ? <> Clients du mois : {currentClients.join(', ')}.</> : <> Aucun client ce mois.</>}
            </>
          )}
        </div>

        <div className="flex gap-1 bg-raise rounded-lg p-0.5 mb-3">
          {([['client', 'Client'], ['absence', 'Absence / autre']] as const).map(([v, l]) => (
            <button key={v} onClick={() => setMode(v)} className={`flex-1 text-[12px] font-semibold px-3 py-1 rounded-md ${mode === v ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{l}</button>
          ))}
        </div>

        {mode === 'client' ? (
          <select value={clientId} onChange={e => setClientId(e.target.value)} className="w-full rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] mb-3">
            <option value="">— choisir un client —</option>
            {gtmClients.length > 0 && <optgroup label="GTM (continu)">{gtmClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
            {ccClients.length > 0 && <optgroup label="Cold call">{ccClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
          </select>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {DAY_TYPES.map(t => <button key={t.v} onClick={() => setDayType(t.v)} className={`text-[12px] px-2.5 py-1 rounded-md border ${dayType === t.v ? 'bg-st-warn text-white border-st-warn' : 'border-line-2 text-dim hover:border-ink'}`}>{t.icon} {t.l}</button>)}
            </div>
            {dayType === 'autre' && <input value={autreText} onChange={e => setAutreText(e.target.value)} placeholder="Ex. Goal Volant, Événement…" className="w-full rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] mb-3" />}
          </>
        )}

        {/* Récurrence : jours de la semaine + portée */}
        <label className="font-mono text-[9px] uppercase tracking-widest text-faint block mb-1">Jours</label>
        <div className="flex gap-1.5 mb-2">
          {WEEKDAYS.map(w => <button key={w.n} onClick={() => toggleWd(w.n)} className={`text-[12px] w-11 py-1 rounded-md border ${weekdays.includes(w.n) ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{w.l}</button>)}
        </div>
        <div className="flex gap-1 bg-raise rounded-lg p-0.5 mb-3 w-max">
          {([['week', 'Cette semaine'], ['month', 'Tout le mois']] as const).map(([v, l]) => (
            <button key={v} onClick={() => setScope(v)} className={`text-[11.5px] font-semibold px-3 py-1 rounded-md ${scope === v ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{l}</button>
          ))}
        </div>

        {mode === 'client' && (
          <div className="flex gap-1.5 mb-3">
            {LOCATIONS.map(l => <button key={l.v} onClick={() => setLocation(l.v)} className={`text-[12px] px-2.5 py-1 rounded-md border ${location === l.v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{l.l}</button>)}
          </div>
        )}

        {/* Aperçu des jours ciblés + dispo */}
        {targetDates.length > 0 && (
          <div className="border border-line rounded-lg p-2 mb-3 max-h-40 overflow-y-auto">
            <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1.5">{targetDates.length} jour(s) ciblé(s) · {nEligible} applicable(s)</div>
            <div className="flex flex-wrap gap-1">
              {targetDates.map(d => {
                const st = mode === 'client' ? statusOf(d) : { s: 'free' as const, label: '' };
                const gtmStack = isGtm && st.s === 'client';           // jour à empiler (GTM)
                const gtmFull = gtmStack && clientsOn(d) >= GTM_MAX;    // ...mais déjà 4/4
                const tone = st.s === 'free' ? 'bg-st-ok/10 text-st-ok'
                  : st.s === 'busy' ? 'bg-raise text-faint'
                  : gtmFull ? 'bg-st-bad/10 text-st-bad'
                  : gtmStack ? 'bg-st-ok/10 text-st-ok'
                  : st.s === 'client' ? 'bg-st-bad/10 text-st-bad'
                  : 'bg-st-warn/10 text-st-warn';
                const dd = new Date(d + 'T00:00:00');
                const lbl = gtmStack ? `${clientsOn(d)}/${GTM_MAX}${gtmFull ? ' plein' : ''}` : st.label;
                return <span key={d} className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${tone}`} title={st.label}>{dd.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' })}{mode === 'client' && st.s !== 'free' ? ` · ${lbl}` : ''}</span>;
              })}
            </div>
          </div>
        )}

        {error && <p className="text-st-bad text-[12px] mb-3">{error}</p>}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-md border border-line-2 text-dim hover:border-ink">Annuler</button>
          <button onClick={submit} disabled={saving || sdrCells == null} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-md bg-hook-rose text-white hover:opacity-90 disabled:opacity-50">{saving ? '…' : `Affecter ${nEligible || ''} jour(s)`}</button>
        </div>
      </div>
    </div>
  );
};

export default AgendaPage;
